<div class="">
  @if (label) {
    <label class="block text-gray-700 text-sm font-bold mb-2">
      {{ label }}
      @if (required) {
        <span class="text-red-500">*</span>
      }
    </label>
  }

  <div>
    <!-- Container for button and dropdown -->
    <div class="relative">
      <!-- Main select button -->
      <button
        type="button"
        [class]="'relative w-full bg-white border rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-11' + (control?.invalid && control?.touched ? ' border-red-500' : ' border-gray-300')"
        (click)="toggleOpen()"
        [disabled]="loading || searchLoading"
      >
        <span class="block truncate">{{ getDisplayText() }}</span>

        <!-- Clear button for multiple selections -->
        @if (hasSelectedOptions() && multiple) {
          <div class="absolute inset-y-0 right-8 flex items-center pr-2">
            <button
              type="button"
              class="text-gray-400 hover:text-gray-600"
              (click)="clearAll()"
            >
              <lucide-angular [img]="XCircleIcon" class="w-4 h-4"></lucide-angular>
            </button>
          </div>
        }

        <!-- Dropdown arrow -->
        <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <lucide-angular
            [img]="ChevronDownIcon"
            class="w-5 h-5 text-gray-400"
            [class.animate-spin]="loading || searchLoading"
          ></lucide-angular>
        </div>
      </button>

      <!-- Dropdown menu -->
      @if (isOpen) {
        <div
          class="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-base border border-gray-300 overflow-auto focus:outline-none sm:text-sm"
          [style.maxHeight]="dropdownMaxHeight"
          (scroll)="onScroll($event)"
        >
        <!-- Search input -->
        <div class="sticky top-0 z-20 bg-white border-b border-gray-200 p-2 shadow-sm">
          <div class="relative">
            <input
              #searchInput
              type="text"
              class="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              [placeholder]="searchPlaceholder"
              [(ngModel)]="searchQuery"
              (input)="onSearchChange()"
              autocomplete="off"
            >
            <div class="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
              <lucide-angular
                [img]="SearchIcon"
                class="w-4 h-4 text-gray-400"
                [class.animate-pulse]="searchLoading"
              ></lucide-angular>
            </div>
          </div>
        </div>

        <!-- Loading state -->
        @if (searchLoading && options.length === 0) {
          <div class="px-3 py-2 text-sm text-gray-500 text-center">
            Searching...
          </div>
        }

        <!-- No results -->
        @if (!searchLoading && options.length === 0 && searchQuery.length >= minSearchLength) {
          <div class="px-3 py-2 text-sm text-gray-500 text-center">
            No results found
          </div>
        }

        <!-- Search prompt -->
        @if (!searchLoading && searchQuery.length < minSearchLength && searchQuery.length > 0) {
          <div class="px-3 py-2 text-sm text-gray-500 text-center">
            Type {{ minSearchLength - searchQuery.length }} more character{{ minSearchLength - searchQuery.length === 1 ? '' : 's' }} to search
          </div>
        }

        <!-- Options list -->
        <div>
          @for (option of options; track option.id) {
            <button
              type="button"
              class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none flex items-start gap-3 group cursor-pointer rounded"
              [class.bg-blue-50]="option.selected"
              [class.text-blue-600]="option.selected"
              (mousedown)="select(option.id); $event.preventDefault(); $event.stopPropagation()"
            >
              <div class="flex-1 min-w-0">
                <span class="block font-medium text-slate-800 group-hover:text-slate-900 whitespace-normal break-words leading-snug">
                  {{ (option.meta?.name) ?? option.label }}
                </span>
              </div>
              <div class="flex items-center gap-2 shrink-0 ml-auto min-w-[170px] justify-end text-right" *ngIf="showMeta && (option.meta?.price || option.meta?.badges?.length)">
                @if (option.meta?.badges?.length) {
                  <div class="relative flex items-center gap-1 text-slate-500 w-14 justify-end group/badges">
                    @if (option.meta?.badges?.includes('minSpend')) {
                      <lucide-angular [img]="PiggyBankIcon" class="w-4 h-4"></lucide-angular>
                    }
                    @if (option.meta?.badges?.includes('qualifying')) {
                      <lucide-angular [img]="StarIcon" class="w-4 h-4"></lucide-angular>
                    }
                    @if (option.meta?.badges?.includes('offer')) {
                      <lucide-angular [img]="GiftIcon" class="w-4 h-4"></lucide-angular>
                    }
                    <div class="pointer-events-none absolute right-0 bottom-full mb-1 hidden group-hover/badges:flex bg-slate-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10">
                      {{ badgeTitle(option.meta?.badges) }}
                    </div>
                  </div>
                } @else {
                  <div class="w-14"></div>
                }

                <span class="text-slate-700 tabular-nums w-16 text-right group-hover:text-slate-900 order-2">
                  {{ option.meta?.price || '£0.00' }}
                </span>

              <div class="flex items-center gap-2 ml-1 order-3" *ngIf="showMeta && (option.meta?.price || option.meta?.badges?.length)">
              <!-- Link icon for options with hyperlinks -->
              @if (enableOptionLinks && generateOptionLink(option)) {
                <span
                  class="text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 rounded cursor-pointer inline-flex items-center"
                  (click)="onOptionLinkClick(option, $event)"
                      title="Open details"
                      role="button"
                      tabindex="-1"
                    >
                      <lucide-angular [img]="ExternalLinkIcon" class="w-4 h-4"></lucide-angular>
                    </span>
                  }
                  
                  <!-- Selected checkmark -->
                  @if (option.selected) {
                    <lucide-angular [img]="CheckIcon" class="w-4 h-4 text-blue-600"></lucide-angular>
                  }
                </div>
              </div>
            </button>
          }
        </div>

        <!-- Load more indicator -->
        @if (loadingMore && enableInfiniteScroll) {
          <div class="px-3 py-2 text-sm text-gray-500 text-center border-t border-gray-200">
            <div class="flex items-center justify-center">
              <div class="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2"></div>
              Loading more...
            </div>
          </div>
        }

        <!-- Load more button (fallback if auto-scroll doesn't work) -->
        @if (hasMore && !loadingMore && !searchLoading && enableInfiniteScroll && options.length > 0) {
          <div class="border-t border-gray-200 mt-1">
            <button
              type="button"
              class="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
              (click)="loadMore()"
            >
              Load more options...
            </button>
          </div>
        }

        <!-- Create new option -->
        @if (allowCreate && searchQuery && !creating) {
          <div class="border-t border-gray-200 mt-1">
            <button
              type="button"
              class="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
              (click)="createOption()"
            >
              {{ createPlaceholder || 'Create "' + searchQuery + '"' }}
            </button>
          </div>
        }
      </div>
    }
    </div>

    <!-- Selected items display for multiple selection -->
    @if (multiple && hasSelectedOptions()) {
      <div class="mt-2">
        <div class="flex flex-wrap gap-1">
          @for (option of selectedOptions(); track option.id) {
            <span class="inline-flex items-center rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <!-- Make entire badge clickable if links are enabled -->
              @if (enableOptionLinks && generateOptionLink(option)) {
                <button
                  type="button"
                  class="flex items-center px-2 py-1 rounded-l-full hover:bg-blue-200 transition-colors duration-200 cursor-pointer"
                  (click)="onOptionLinkClick(option, $event)"
                  title="Click to open contact details"
                >
                  <span>{{ option.label }}</span>
                </button>
              } @else {
                <span class="px-2 py-1">{{ option.label }}</span>
              }
              
              <!-- Remove button -->
              <button
                type="button"
                class="px-2 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-r-full transition-colors duration-200"
                (click)="deselect(option.id)"
                title="Remove contact"
              >
                <lucide-angular [img]="XIcon" class="w-3 h-3"></lucide-angular>
              </button>
            </span>
          }
        </div>
      </div>
    }
  </div>

  <!-- Error message -->
  @if (control?.invalid && control?.touched) {
    <div class="text-red-500 text-xs mt-1">
      @if (control?.errors?.['required']) {
        <div>This field is required.</div>
      }
    </div>
  }
</div>
